<template>
    <div class="habits-view">
        <skeleton-loader v-if="loading" type="book-shift" />

        <div v-else-if="!can('habits')" class="hab-empty">
            <v-icon size="40" class="mb-2">mdi-lock-outline</v-icon>
            <div>{{ $t('plans.student_notice') }}</div>
        </div>

        <div v-else-if="!assignment" class="hab-empty">
            <v-icon size="40" class="mb-2">mdi-checkbox-marked-outline</v-icon>
            <div>{{ $t('habits.student_empty') }}</div>
        </div>

        <template v-else>
            <div>
                <div class="hab-sec-hd">
                    <div class="section-title">{{ $t('habits.register') }}</div>
                    <button-tooltip :tooltip="$t('habits.streak_tooltip')" top>
                        <template v-slot:activator="{ on, attrs }">
                            <span class="hab-streak" v-bind="attrs" v-on="on">🔥 {{ $tc('habits.streak_days', streak, { n: streak }) }}</span>
                        </template>
                    </button-tooltip>
                </div>

                <v-slide-group ref="strip" v-model="selectedDate" class="day-group" mandatory show-arrows="desktop">
                    <v-slide-item v-for="item in stripDays" :key="item.date" :value="item.date" :disabled="!item.enabled"
                        v-slot="{ active, toggle }">
                        <button type="button" class="day-tab" :class="{ 'day-tab--active': active, 'day-tab--off': !item.enabled }"
                            @click="toggle">
                            <span v-if="item.md" class="day-md">MD</span>
                            <span class="day-name">{{ item.label }}</span>
                            <span class="day-num">{{ item.number }}</span>
                            <span class="day-chip" :class="`day-chip--${item.status}`">{{ item.chip }}</span>
                        </button>
                    </v-slide-item>
                </v-slide-group>

                <div class="hab-card">
                    <div class="hab-card-top">
                        <div class="hab-card-row">
                            <span><strong>{{ $t('habits.x_of_y', { x: selectedProgress.done, y: selectedProgress.total }) }}</strong> {{ $t('habits.completed') }}</span>
                            <span class="hab-card-day">{{ selectedDayLabel }}</span>
                        </div>
                        <v-progress-linear :value="selectedPercent" height="6" rounded color="accent" background-color="outline" />
                    </div>

                    <div v-if="!selectedHabits.length" class="hab-none">{{ $t('habits.no_habits_day') }}</div>
                    <div v-else class="hab-list">
                        <div v-for="habit in selectedHabits" :key="habit.key" class="hab-row"
                            @click="habit.type === 'bool' ? toggleBool(habit) : openRegister(habit)">
                            <habit-icon :category="habit.category" :size="36" />
                            <div class="hab-info">
                                <div class="hab-name">{{ habitName(habit) }}</div>
                                <div class="hab-sub">
                                    <span v-if="habit.frequency.kind === 'md'" class="hab-tag">{{ $t('habits.today_is', { md: habitFrequencyLabel(habit.frequency) }) }}</span>
                                    <span>{{ habitSubtext(habit) }}</span>
                                </div>
                            </div>
                            <button v-if="habit.type === 'bool'" type="button" class="hab-check"
                                :class="{ 'hab-check--on': isDone(habit) }" @click.stop="toggleBool(habit)">
                                <v-icon size="22">mdi-check</v-icon>
                            </button>
                            <button v-else type="button" class="hab-load" :class="{ 'hab-load--done': isDone(habit) }"
                                @click.stop="openRegister(habit)">
                                <template v-if="isDone(habit)"><v-icon size="16">mdi-check</v-icon> {{ $t('habits.done') }}</template>
                                <template v-else>+ {{ $t('habits.load') }}</template>
                            </button>
                        </div>
                    </div>
                    <div v-if="selectedProgress.total && selectedProgress.done >= selectedProgress.total" class="hab-done-msg">
                        {{ $t('habits.all_done') }} 🎉
                    </div>
                </div>
                <div class="hab-hint">{{ $t('habits.days_hint') }}</div>
            </div>

            <div>
                <div class="section-title">{{ $t('habits.your_combo') }}</div>
                <div class="hab-combo">
                    <div class="hab-combo-name">{{ assignment.combo.name }}</div>
                    <div class="hab-combo-meta">{{ comboMeta }}</div>
                    <div v-if="assignment.combo.notes" class="hab-note">
                        <b>{{ $t('habits.coach_note') }}:</b> {{ assignment.combo.notes }}
                    </div>
                    <div v-for="item in upcomingMd" :key="item.key" class="hab-upcoming">
                        <span class="hab-tag">{{ item.md }}</span> {{ item.name }} · {{ item.when }}
                    </div>
                </div>
            </div>

            <div>
                <div class="section-title">{{ $t('habits.your_numbers') }}</div>
                <div class="stats-row">
                    <home-stat-card :value="`🔥 ${streak}`" :label="$t('habits.streak_label')" value-class="green-color"
                        :delta="$t('habits.record', { n: bestStreak })" />
                    <home-stat-card :value="weekAdherence === null ? '-' : `${weekAdherence}%`" :label="$t('habits.this_week')"
                        value-class="teal-color" :delta="weekDelta.text" :delta-state="weekDelta.state" />
                    <home-stat-card :value="assignment.combo.habits.length" :label="$t('habits.title')"
                        :delta="$t('habits.in_your_combo')" />
                </div>
            </div>

            <div>
                <div class="section-title">{{ $t('habits.last_4_weeks') }}</div>
                <div class="hab-cal">
                    <div class="hab-cal-grid">
                        <span v-for="(letter, i) in weekLetters" :key="`l${i}`" class="hab-cal-hd">{{ letter }}</span>
                        <button-tooltip v-for="cell in calendarCells" :key="cell.date" :tooltip="cell.tooltip" top :disabled="!cell.tooltip">
                            <template v-slot:activator="{ on, attrs }">
                                <span class="hab-cal-cell" :class="`hab-cal-cell--${cell.status}`" v-bind="attrs" v-on="on">{{ cell.day }}</span>
                            </template>
                        </button-tooltip>
                    </div>
                    <div class="hab-cal-leg">
                        <span><i class="leg-full"></i>{{ $t('habits.legend_all') }}</span>
                        <span><i class="leg-partial"></i>{{ $t('habits.legend_part') }}</span>
                        <span><i class="leg-empty"></i>{{ $t('habits.legend_none') }}</span>
                    </div>
                </div>
            </div>

            <div>
                <div class="section-title">{{ $t('habits.reminders') }}</div>
                <div class="hab-pref">
                    <div class="hab-pref-row">
                        <div class="hab-pref-txt">
                            <div class="hab-pref-t">{{ $t('habits.single_reminder') }}</div>
                            <div class="hab-pref-s">{{ $t('habits.single_reminder_hint') }}</div>
                        </div>
                        <kimsa-toggle :value="singleReminder" @change="setPrefs({ singleReminder: $event })" />
                    </div>
                    <div class="hab-pref-row" :class="{ 'hab-pref-row--off': !singleReminder }">
                        <div class="hab-pref-txt">
                            <div class="hab-pref-t">{{ $t('habits.reminder_hour') }}</div>
                            <div class="hab-pref-s">{{ $t('habits.reminder_hour_hint') }}</div>
                        </div>
                        <hour-clock-picker :value="reminderHour" :disabled="!singleReminder"
                            @change="setPrefs({ reminderHour: $event })" />
                    </div>
                </div>
            </div>
        </template>

        <habit-register-dialog ref="register" @save="onRegisterSave" />
    </div>
</template>

<script>
import moment from 'moment'
import { db } from '@/utils/Firebase'
import {
    DATE_FORMAT, EDITABLE_DAYS, today, isAssignmentActive, loadWeeks, daysFromWeeks, loadCompetenceDates,
    mdMapFromCompetences, habitAppliesOn, habitDone, habitGoal, dayProgress, dayStatus, weeklyDoneCount,
    weekStartOf, weekDates, computeWeekSummary, computeStreak, saveHabitValue, pushSchedule, mdLabel,
} from '@/utils/Habits'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import KimsaToggle from '@/components/common/inputs/KimsaToggle'
import HomeStatCard from '@/components/student/home/HomeStatCard'
import HabitIcon from '@/components/habits/HabitIcon.vue'
import HourClockPicker from '@/components/habits/HourClockPicker.vue'
import HabitRegisterDialog from '@/components/habits/HabitRegisterDialog.vue'
import habitsMixin from '@/mixins/habitsMixin'

export default {
    name: 'StudentHabits',
    mixins: [habitsMixin],
    components: { SkeletonLoader, ButtonTooltip, KimsaToggle, HomeStatCard, HabitIcon, HourClockPicker, HabitRegisterDialog },
    data() {
        return {
            loading: true,
            assignment: null,
            coachName: '',
            days: {},
            mdMap: {},
            selectedDate: today(),
            today: today(),
        }
    },
    computed: {
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        habits(vm = this) {
            return vm.assignment?.combo?.habits || []
        },
        goals(vm = this) {
            return vm.assignment?.goals || {}
        },
        stripDays(vm = this) {
            return Array.from({ length: EDITABLE_DAYS + 1 }, (_, i) => {
                const m = moment(vm.today, DATE_FORMAT).subtract(EDITABLE_DAYS - i, 'days')
                const date = m.format(DATE_FORMAT)
                const enabled = vm.inRange(date)
                const progress = enabled ? dayProgress(vm.habits, date, vm.days[date], vm.mdMap, vm.goals) : { total: 0, done: 0 }
                return {
                    date,
                    enabled,
                    label: date === vm.today ? vm.$t('common.today') : m.format('ddd'),
                    number: m.format('D'),
                    md: (vm.mdMap[date] || []).includes(0),
                    status: enabled ? dayStatus(progress) : 'none',
                    chip: progress.total ? `${progress.done}/${progress.total}` : '–',
                }
            })
        },
        selectedHabits(vm = this) {
            return vm.habits.filter(habit => habitAppliesOn(habit, vm.selectedDate, vm.mdMap))
        },
        selectedProgress(vm = this) {
            return dayProgress(vm.habits, vm.selectedDate, vm.days[vm.selectedDate], vm.mdMap, vm.goals)
        },
        selectedPercent(vm = this) {
            const { done, total } = vm.selectedProgress
            return total ? (done / total) * 100 : 0
        },
        selectedDayLabel(vm = this) {
            const m = moment(vm.selectedDate, DATE_FORMAT)
            const label = m.format('dddd D [de] MMMM')
            const parts = [vm.selectedDate === vm.today ? `${vm.$t('common.today')} · ${label}` : label]
            const offsets = vm.mdMap[vm.selectedDate] || []
            if (offsets.length) parts.push(offsets.map(offset => mdLabel(offset)).join(' / '))
            return parts.join(' · ')
        },
        streak(vm = this) {
            if (!vm.assignment) return 0
            return computeStreak(vm.habits, vm.goals, vm.days, vm.mdMap, vm.assignment.startDate, vm.today)
        },
        bestStreak(vm = this) {
            return Math.max(vm.streak, vm.assignment?.streak?.best || 0)
        },
        weekAdherence(vm = this) {
            return vm.summaryFor(weekStartOf(vm.today)).adherence
        },
        weekDelta(vm = this) {
            const previous = vm.summaryFor(moment(weekStartOf(vm.today), DATE_FORMAT).subtract(7, 'days').format(DATE_FORMAT)).adherence
            if (vm.weekAdherence === null || previous === null) return { text: '', state: 'neutral' }
            const diff = vm.weekAdherence - previous
            return {
                text: vm.$t('habits.points', { n: Math.abs(diff) }),
                state: diff > 0 ? 'up' : (diff < 0 ? 'down' : 'neutral'),
            }
        },
        comboMeta(vm = this) {
            const a = vm.assignment
            const parts = []
            if (vm.coachName) parts.push(vm.$t('habits.assigned_by', { name: vm.coachName }))
            if (a.endDate) {
                const total = Math.ceil((moment(a.endDate).diff(moment(a.startDate), 'days') + 1) / 7)
                const current = Math.min(total, Math.floor(moment(vm.today).diff(moment(a.startDate), 'days') / 7) + 1)
                parts.push(vm.$t('habits.week_x_of_y', { x: current, y: total }))
                parts.push(vm.$t('habits.until', { date: moment(a.endDate).format('D [de] MMM') }))
            }
            return parts.join(' · ')
        },
        upcomingMd(vm = this) {
            const out = []
            for (let i = 1; i <= 7; i++) {
                const date = moment(vm.today, DATE_FORMAT).add(i, 'days').format(DATE_FORMAT)
                vm.habits.filter(habit => habit.frequency.kind === 'md' && habitAppliesOn(habit, date, vm.mdMap))
                    .forEach(habit => {
                        if (out.some(item => item.key === habit.key)) return
                        out.push({
                            key: habit.key,
                            md: mdLabel(habit.frequency.offset),
                            name: vm.habitName(habit),
                            when: i === 1 ? vm.$t('common.tomorrow') : moment(date).format('dddd'),
                        })
                    })
            }
            return out
        },
        weekLetters(vm = this) {
            return Array.from({ length: 7 }, (_, i) => moment().isoWeekday(i + 1).format('dd').charAt(0).toUpperCase())
        },
        calendarCells(vm = this) {
            const start = moment(weekStartOf(vm.today), DATE_FORMAT).subtract(21, 'days')
            return Array.from({ length: 28 }, (_, i) => {
                const m = start.clone().add(i, 'days')
                const date = m.format(DATE_FORMAT)
                if (date > vm.today || !vm.inRange(date)) return { date, day: m.format('D'), status: 'none', tooltip: '' }
                const progress = dayProgress(vm.habits, date, vm.days[date], vm.mdMap, vm.goals)
                const status = dayStatus(progress)
                return {
                    date,
                    day: m.format('D'),
                    status,
                    tooltip: progress.total ? `${m.format('ddd D MMM')} · ${progress.done}/${progress.total}` : '',
                }
            })
        },
        singleReminder(vm = this) {
            return !!vm.assignment?.studentPrefs?.singleReminder
        },
        reminderHour(vm = this) {
            const prefs = vm.assignment?.studentPrefs || {}
            return Number(prefs.reminderHour ?? vm.assignment?.combo?.alarms?.dailyHour ?? 20)
        },
    },
    async mounted() {
        await this.load()
    },
    methods: {
        inRange(date) {
            const a = this.assignment
            if (!a) return false
            if (a.startDate && date < a.startDate) return false
            if (a.endDate && date > a.endDate) return false
            return true
        },
        async load() {
            const vm = this
            vm.loading = true
            try {
                if (!vm.studentId || !vm.can('habits')) return
                const snap = await db.collection('habitAssignments').doc(vm.studentId).get()
                const data = snap.exists ? snap.data() : null
                const editableFrom = moment(vm.today, DATE_FORMAT).subtract(EDITABLE_DAYS, 'days').format(DATE_FORMAT)
                const recentlyEnded = !!data?.endDate && data.endDate >= editableFrom && data.startDate <= vm.today
                if (!data || !(isAssignmentActive(data, vm.today) || recentlyEnded)) {
                    vm.assignment = null
                    return
                }
                vm.assignment = { ...data, id: snap.id }
                const from = moment(vm.today, DATE_FORMAT).subtract(34, 'days').format(DATE_FORMAT)
                const to = moment(vm.today, DATE_FORMAT).add(8, 'days').format(DATE_FORMAT)
                const [weeks, competences, coach] = await Promise.all([
                    loadWeeks(vm.studentId, from, vm.today),
                    loadCompetenceDates(vm.studentId, from, to).catch(() => []),
                    db.collection('users').doc(data.coachId).get().catch(() => null),
                ])
                vm.days = daysFromWeeks(weeks)
                vm.mdMap = mdMapFromCompetences(competences)
                const coachData = coach?.data?.() || {}
                vm.coachName = [coachData.name, coachData.lastName].filter(Boolean).join(' ')
                if (!vm.inRange(vm.selectedDate)) vm.selectedDate = vm.stripDays.filter(day => day.enabled).pop()?.date || vm.today
                vm.syncTimezone()
            } finally {
                vm.loading = false
            }
        },
        syncTimezone() {
            const vm = this
            const offset = moment().utcOffset()
            if (vm.assignment.utcOffset === offset) return
            vm.updateAssignment(pushSchedule(vm.assignment, offset))
        },
        async updateAssignment(changes) {
            const vm = this
            vm.assignment = { ...vm.assignment, ...changes }
            await db.collection('habitAssignments').doc(vm.studentId).update(changes).catch(err => console.log(err))
        },
        setPrefs(prefs) {
            const vm = this
            const studentPrefs = { ...(vm.assignment.studentPrefs || {}), reminderHour: vm.reminderHour, ...prefs }
            const next = { ...vm.assignment, studentPrefs }
            vm.updateAssignment({ studentPrefs, ...pushSchedule(next, moment().utcOffset()) })
        },
        valueOf(habit, date = this.selectedDate) {
            return this.days[date]?.values?.[habit.key]?.v
        },
        isDone(habit) {
            return habitDone(habit, this.valueOf(habit), this.goals)
        },
        habitSubtext(habit) {
            const vm = this
            const parts = []
            if (habit.type !== 'bool') {
                const value = vm.valueOf(habit)
                const goal = habitGoal(habit, vm.goals)
                const unit = vm.habitUnitLabel(habit.unit)
                parts.push(`${vm.formatHabitNumber(value || 0)} / ${vm.formatHabitNumber(goal)} ${unit}`.trim())
            }
            if (habit.frequency.kind === 'weekly') {
                const weekDays = {}
                weekDates(weekStartOf(vm.selectedDate)).forEach(date => { weekDays[date] = vm.days[date] })
                const done = weeklyDoneCount(habit, weekDays, vm.goals)
                parts.push(done >= habit.frequency.times
                    ? `✓ ${vm.$t('habits.week_quota_done')}`
                    : vm.$t('habits.week_quota', { x: done, y: habit.frequency.times }))
            }
            if (habit.baseId === 'sleep_hours' && vm.valueOf(habit)) parts.push(vm.$t('habits.last_night', { n: vm.formatHabitNumber(vm.valueOf(habit)) }))
            return parts.join(' · ')
        },
        summaryFor(weekStart) {
            const vm = this
            if (!vm.assignment) return { adherence: null }
            const days = {}
            weekDates(weekStart).forEach(date => { if (vm.days[date]) days[date] = vm.days[date] })
            return computeWeekSummary({
                habits: vm.habits, goals: vm.goals, days, mdDays: vm.mdMap, weekStart,
                startDate: vm.assignment.startDate, endDate: vm.assignment.endDate, until: vm.today,
            })
        },
        toggleBool(habit) {
            this.setValue(habit, this.isDone(habit) ? null : true)
        },
        openRegister(habit) {
            this.$refs.register.show(habit, this.valueOf(habit), this.goals)
        },
        onRegisterSave({ habit, value }) {
            this.setValue(habit, value)
        },
        async setValue(habit, value) {
            const vm = this
            const date = vm.selectedDate
            const previous = vm.days[date] ? JSON.parse(JSON.stringify(vm.days[date])) : null
            const values = { ...(vm.days[date]?.values || {}) }
            if (value === null) delete values[habit.key]
            else values[habit.key] = { v: value, at: new Date() }
            vm.$set(vm.days, date, { ...(vm.days[date] || {}), values })
            try {
                await saveHabitValue({
                    assignment: vm.assignment, studentId: vm.studentId, date, habitKey: habit.key, value,
                    days: vm.days, mdMap: vm.mdMap,
                })
                vm.syncStreak()
            } catch (err) {
                console.log(err)
                if (previous) vm.$set(vm.days, date, previous)
                else vm.$delete(vm.days, date)
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        syncStreak() {
            const vm = this
            const stored = vm.assignment.streak || {}
            if (stored.count === vm.streak && (stored.best || 0) >= vm.bestStreak) return
            vm.updateAssignment({ streak: { count: vm.streak, best: vm.bestStreak, lastDate: vm.today } })
        },
    },
    watch: {
        loading(val) {
            if (val) return
            this.$nextTick(() => {
                const strip = this.$refs.strip
                if (strip?.$refs?.content?.parentElement) strip.$refs.content.parentElement.scrollLeft = 9999
            })
        },
    },
}
</script>

<style scoped>
.habits-view {
    max-width: 700px;
    margin: 0 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-bottom: 24px;
}

.section-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: .06em;
    margin-bottom: 10px;
}

.hab-sec-hd {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.hab-streak {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-success-base);
    margin-bottom: 10px;
}

.hab-empty {
    padding: 60px 20px;
    text-align: center;
    font-size: 14px;
    color: var(--v-txtMuted-base);
}

.hab-empty .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.day-group {
    margin-bottom: 12px;
}

.day-tab {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    min-width: 58px;
    padding: 8px 10px;
    margin-right: 6px;
    border-radius: 12px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    transition: border-color .15s, color .15s;
}

.day-tab--off {
    opacity: .4;
}

.day-name {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: var(--v-txtMuted-base);
}

.day-num {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.1;
    color: var(--v-txtPrimary-base);
}

.day-md {
    position: absolute;
    top: -6px;
    right: -4px;
    padding: 1px 4px;
    border-radius: 4px;
    font-size: 8px;
    font-weight: 800;
    background: var(--v-primary-base);
    color: #0C0E12;
}

.day-chip {
    font-size: 10px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    color: var(--v-txtMuted-base);
}

.day-chip--full {
    background: rgba(0, 153, 102, .15);
    color: var(--v-success-base);
}

.day-chip--partial {
    background: rgba(232, 192, 0, .15);
    color: #E8C000;
}

.day-chip--empty {
    background: rgba(232, 92, 92, .15);
    color: var(--v-error-base);
}

.day-tab--active {
    border-color: var(--v-accent-base);
}

.day-tab--active .day-name,
.day-tab--active .day-num {
    color: var(--v-accent-base);
}

.hab-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
}

.hab-card-top {
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-bottom: 1px solid var(--v-outline-base);
}

.hab-card-row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 13px;
    color: var(--v-txtSecondary-base);
}

.hab-card-row strong {
    color: var(--v-txtPrimary-base);
}

.hab-card-day {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    text-transform: capitalize;
    text-align: right;
}

.hab-none,
.hab-done-msg {
    padding: 20px 16px;
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

.hab-done-msg {
    color: var(--v-success-base);
    font-weight: 600;
    border-top: 1px solid var(--v-outline-base);
}

.hab-row {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 60px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--v-outline-base);
    cursor: pointer;
}

.hab-row:last-child {
    border-bottom: none;
}

.hab-info {
    flex: 1;
    min-width: 0;
}

.hab-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.hab-sub {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.hab-tag {
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 800;
    background: rgba(204, 255, 51, .12);
    color: var(--v-primary-base);
}

.theme--light .hab-tag {
    background: rgba(51, 153, 153, .12);
    color: var(--v-accent-base);
}

.hab-check {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 2px solid var(--v-outline2-base);
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    transition: all .15s;
}

.hab-check .v-icon {
    color: transparent !important;
}

.hab-check--on {
    background: var(--v-success-base);
    border-color: var(--v-success-base);
}

.hab-check--on .v-icon {
    color: #fff !important;
}

.hab-load {
    height: 44px;
    padding: 0 14px;
    border-radius: 22px;
    border: 1px solid var(--v-outline2-base);
    background: none;
    color: var(--v-txtPrimary-base);
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.hab-load--done {
    border-color: rgba(0, 153, 102, .5);
    color: var(--v-success-base);
}

.hab-load--done .v-icon {
    color: inherit !important;
}

.hab-hint {
    margin-top: 8px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.hab-combo {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.hab-combo-name {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.hab-combo-meta {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.hab-note {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    background: var(--v-surface2-base);
    border-radius: 10px;
    padding: 10px 12px;
}

.hab-upcoming {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.hab-cal {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 14px 16px;
}

.hab-cal-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
}

.hab-cal-hd {
    text-align: center;
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
}

.hab-cal-cell {
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    background: var(--v-surface2-base);
}

.hab-cal-cell--full {
    background: var(--v-success-base);
    color: #fff;
}

.hab-cal-cell--partial {
    background: #E8C000;
    color: #0C0E12;
}

.hab-cal-cell--empty {
    background: rgba(232, 92, 92, .8);
    color: #fff;
}

.hab-cal-leg {
    display: flex;
    gap: 14px;
    margin-top: 10px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.hab-cal-leg i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 3px;
    margin-right: 4px;
    vertical-align: -1px;
}

.leg-full {
    background: var(--v-success-base);
}

.leg-partial {
    background: #E8C000;
}

.leg-empty {
    background: rgba(232, 92, 92, .8);
}

.hab-pref {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
}

.hab-pref-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.hab-pref-row:last-child {
    border-bottom: none;
}

.hab-pref-row--off {
    opacity: .5;
}

.hab-pref-txt {
    flex: 1;
}

.hab-pref-t {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.hab-pref-s {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}
</style>
